/**
 * User Engagement Overview Component
 * Displays user engagement metrics in a card layout
 */

import {
  Box,
  Paper,
  Card,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { UserIcon } from 'src/v3/core/icons'
import { UserEngagement } from '../../types'

interface UserEngagementOverviewProps {
  data: UserEngagement
}

export default function UserEngagementOverview({
  data,
}: UserEngagementOverviewProps) {
  let theme = useTheme()

  let metrics = [
    {
      label: 'Avg Hours/User',
      value: data.averageHoursPerUser.toFixed(1),
      tooltip: 'Among active users',
      color: theme.palette.analyticsColors.ceHours,
    },
    {
      label: 'Total Activities',
      value: data.totalActivities.toString(),
      tooltip: 'In selected period',
      color: theme.palette.analyticsColors.activities,
    },
    {
      label: 'Total CE Hours',
      value: (data.totalCeHours || 0).toFixed(0),
      tooltip: 'In selected period',
      color: theme.palette.analyticsColors.ceHours,
    },
  ]

  return (
    <Paper sx={{ p: 3 }}>
      <Typography
        variant="h6"
        sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <UserIcon />
        User Engagement Overview
      </Typography>

      <Box className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {metrics.map((metric) => (
          <Box key={metric.label}>
            <Card
              variant="outlined"
              sx={{ p: 2, textAlign: 'center', height: '100%' }}
            >
              <Typography variant="h3" sx={{ mb: 1, color: metric.color }}>
                {metric.value}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {metric.label}
                </Typography>
                <Tooltip title={metric.tooltip} arrow>
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlined
                      sx={{ fontSize: 16, color: 'text.disabled' }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          </Box>
        ))}
      </Box>
    </Paper>
  )
}
